import { useState, useEffect, useMemo } from "react";
import {
	Loader2,
	Plus,
	Trash2,
	Link2,
	Paperclip,
	MessageSquare,
	MessagesSquare,
	ListChecks,
	Check,
	Link,
	CornerDownRight,
	Bell,
	BellRing,
	X,
	CircleDot,
	User,
	Tag as TagIcon,
	FolderKanban,
	Rocket,
	Calendar,
	Clock,
	Shapes,
	CalendarPlus,
	History,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
	Dialog,
	DialogContent,
	DialogClose,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
	ProjectDescriptionEditor,
	ProjectDescriptionPreview,
} from "@/components/projects/project-description";
import {
	projectDescriptionText,
	toMarkdown,
} from "@/components/projects/project-description-utils";
import { PermissionGate } from "@/components/PermissionGate";
import { ChecklistSection } from "./task-detail/checklist/ChecklistSection";
import { GithubSection } from "./task-detail/github/GithubSection";
import { TaskHistorySection } from "./task-detail/history/TaskHistorySection";
import { usePermission } from "@/hooks/usePermission";
import { useApiSWR } from "@/hooks/useApiSWR";
import { useWorkspaceFeatureFlags } from "@/hooks/useWorkspaceFeatureFlags";
import { type Project } from "@/services/project.service";
import type { WorkflowStage } from "@/services/workflow-stage.service";
import { SYSTEM_STAGES } from "./system-stages";
import { StageChip, StageDot } from "./StageChip";
import {
	listAllTasks,
	type ApiTaskStatus,
	type Task,
	type UpdateTaskPayload,
} from "@/services/task.service";
import { type Profile } from "@/services/profile.service";
import { listSprints, type Sprint } from "@/services/sprint.service";
import {
	listTaskSubStatuses,
	type TaskSubStatus,
} from "@/services/task-sub-status.service";
import { type UiTask, allAssignees, toUiTask } from "./types";
import { TaskAttachments } from "./TaskAttachments";
import { TaskComments } from "./TaskComments";
import {
	MetaRow,
	InlineTitle,
	InlineDescription,
	InlinePriority,
	InlineTaskType,
	InlineAssignee,
	InlineDueDate,
	InlineEstimatedTime,
	InlineLogTime,
	InlineTags,
	InlineSprint,
	InlineProject,
} from "./task-detail";
import { CalendarButton } from "./task-detail/CalendarButton";
import { CopyTextContextMenu } from "./task-detail/CopyTextContextMenu";
import { HoverCopy } from "./task-detail/HoverCopy";
import { EstimateHistoryPopover } from "./task-detail/EstimateHistoryPopover";
import {
	getSubscription,
	subscribe as subscribeTask,
	unsubscribe as unsubscribeTask,
} from "@/services/task-subscription.service";
import { TicketCode } from "@/pages/tasks-v2/components/bits";

function shortTaskRef(task: UiTask, project?: Project): string {
	const prefix = project?.name
		? project.name
				.split(/\s+/)
				.map((w) => w[0])
				.join("")
				.slice(0, 4)
				.toUpperCase()
		: "TASK";
	return `${prefix}-${task.id.slice(0, 4).toUpperCase()}`;
}

interface Props {
	task: UiTask | null;
	projects: Project[];
	profiles?: Profile[];
	stages?: WorkflowStage[];
	onClose: () => void;
	onSaveNotes?: (task: UiTask, notes: string) => Promise<void>;
	onUpdate?: (task: UiTask, payload: UpdateTaskPayload) => Promise<void>;
	onChangeStatus?: (task: UiTask, status: ApiTaskStatus) => Promise<void>;
	onAddChild?: (parent: UiTask) => void;
	onOpenTask?: (task: UiTask) => void;
	onOpenParent?: (parentId: string) => void;
	onEdit?: (task: UiTask) => void;
	onDelete?: (task: UiTask) => void;
	onToggleChildDone?: (child: UiTask) => Promise<void>;
	escEnabled?: boolean;
}

export function TaskDetailDrawerV2({
	task,
	projects,
	profiles = [],
	stages,
	onClose,
	onSaveNotes,
	onUpdate,
	onChangeStatus,
	onAddChild,
	onOpenTask,
	onOpenParent,
	onDelete,
	onToggleChildDone,
	escEnabled = true,
}: Props) {
	const [notes, setNotes] = useState("");
	const [saving, setSaving] = useState(false);
	const [statusSaving, setStatusSaving] = useState(false);
	const [subStatusSaving, setSubStatusSaving] = useState(false);
	const [showNotesEditor, setShowNotesEditor] = useState(false);
	const [confirmingDelete, setConfirmingDelete] = useState(false);
	const [subscribePending, setSubscribePending] = useState(false);
	const { can } = usePermission();
	const { flags } = useWorkspaceFeatureFlags();
	const canEdit = !!onUpdate && can("Create & edit tasks");
	const canEditStatus = !!onChangeStatus && can("Create & edit tasks");

	const open = !!task;
	const taskId = task?.id;
	const taskProjectId = task?.project_id;

	const { data: sprintsData, isLoading: sprintsLoading } = useApiSWR<
		Sprint[]
	>(task ? ["sprints"] : null, listSprints);
	const sprints = sprintsData ?? [];

	const { data: subStatusData } = useApiSWR<TaskSubStatus[]>(
		task ? ["task-sub-statuses"] : null,
		listTaskSubStatuses,
	);
	const subStatuses = useMemo(() => subStatusData ?? [], [subStatusData]);

	const { data: subscribedData, mutate: mutateSubscribed } =
		useApiSWR<boolean>(
			taskId && taskProjectId ? ["tasks", taskId, "subscription"] : null,
			() => getSubscription(taskProjectId!, taskId!),
		);
	const subscribed = subscribedData ?? false;

	useEffect(() => {
		if (task) {
			setNotes(task.developer_notes ?? "");
			setShowNotesEditor(false);
			setConfirmingDelete(false);
		}
	}, [task]);

	// Close on Escape (skip while the delete-confirm modal or an overlapping
	// drawer is open).
	useEffect(() => {
		if (!open || !escEnabled) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape" && !confirmingDelete) onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open, escEnabled, confirmingDelete, onClose]);

	const project = projects.find((p) => p.id === task?.project_id);
	const stageList = stages ?? SYSTEM_STAGES;
	const currentStage = task
		? stageList.find((s) => s.key === task.apiStatus)
		: undefined;

	const availableSubStatuses = useMemo(
		() =>
			task
				? subStatuses.filter((s) => s.parent_status === task.apiStatus)
				: [],
		[subStatuses, task],
	);
	const currentSubStatus = task?.sub_status
		? subStatuses.find((s) => s.key === task.sub_status)
		: undefined;

	// Fetched by parent id: the caller's list may be filtered (sprint/page).
	const { data: childData } = useApiSWR<Task[]>(
		taskId ? ["task-children", taskId] : null,
		() => listAllTasks({ parent_task_id: taskId }),
	);
	const children = useMemo(
		() => (childData ?? []).map(toUiTask),
		[childData],
	);

	const completedCount = children.filter(
		(c) => c.apiStatus === "done",
	).length;
	const progress = children.length
		? Math.round((completedCount / children.length) * 100)
		: 0;

	async function persist(payload: UpdateTaskPayload) {
		if (!task || !onUpdate) return;
		await onUpdate(task, payload);
	}

	async function handleSave() {
		if (!task) return;
		setSaving(true);
		try {
			if (onSaveNotes) {
				await onSaveNotes(task, notes);
			}
			setShowNotesEditor(false);
		} finally {
			setSaving(false);
		}
	}

	async function handleStatusChange(next: string) {
		if (!task || !onChangeStatus) return;
		const nextStatus = next as ApiTaskStatus;
		if (nextStatus === task.apiStatus) return;
		setStatusSaving(true);
		try {
			await onChangeStatus(task, nextStatus);
		} catch (e) {
			toast.error("Failed to update status", {
				description: (e as Error).message || "Please try again.",
			});
		} finally {
			setStatusSaving(false);
		}
	}

	// Clear sub-status when the task's status no longer matches its parent.
	useEffect(() => {
		if (!task || !task.sub_status || !onUpdate) return;
		if (subStatuses.length === 0) return;
		const sub = subStatuses.find((s) => s.key === task.sub_status);
		if (sub && sub.parent_status !== task.apiStatus) {
			onUpdate(task, { sub_status: null }).catch(() => {});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [task?.id, task?.apiStatus, task?.sub_status, subStatuses]);

	async function handleSubStatusChange(next: string) {
		if (!task || !onUpdate) return;
		const value = next || null;
		if (value === task.sub_status) return;
		setSubStatusSaving(true);
		try {
			await persist({ sub_status: value });
		} catch (e) {
			toast.error("Failed to update sub-status", {
				description: (e as Error).message || "Please try again.",
			});
		} finally {
			setSubStatusSaving(false);
		}
	}

	async function handleToggleSubscribe() {
		if (!task || subscribePending) return;
		const next = !subscribed;
		setSubscribePending(true);
		await mutateSubscribed(next, { revalidate: false }); // optimistic
		try {
			if (next) await subscribeTask(task.project_id, task.id);
			else await unsubscribeTask(task.project_id, task.id);
			toast.success(next ? "Subscribed to task" : "Unsubscribed", {
				description: next
					? "You will get this task in your digest email"
					: undefined,
			});
		} catch (e) {
			await mutateSubscribed(!next, { revalidate: false }); // revert
			toast.error("Failed to update subscription", {
				description: (e as Error).message || "Please try again.",
			});
		} finally {
			setSubscribePending(false);
		}
	}

	const taskUrl = task
		? `${window.location.origin}/tasks?taskId=${task.id}&projectId=${task.project_id}`
		: "";

	function handleCopyLink() {
		if (!task) return;
		navigator.clipboard
			.writeText(taskUrl)
			.then(() =>
				toast.success("Link copied", {
					description: "Task URL on clipboard",
				}),
			)
			.catch(() => toast.error("Failed to copy link"));
	}

	return (
		<>
			<div
				className={cn(
					"fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity",
					open ? "opacity-100" : "pointer-events-none opacity-0",
				)}
				onClick={onClose}
			/>
			<aside
				className={cn(
					"fixed right-0 top-0 z-50 flex h-full w-full max-w-4xl flex-col bg-surface shadow-2xl transition-transform duration-300",
					open ? "translate-x-0" : "translate-x-full",
				)}
				aria-hidden={!open}
			>
				{task && (
					<>
						{/* Header */}
						<header className="flex items-center justify-between gap-3 border-b border-border bg-surface px-5 py-3">
							<div className="flex min-w-0 items-center gap-3">
								<span
									className="flex cursor-pointer items-center gap-2 truncate text-sm font-medium text-primary hover:text-primary-hover"
									onClick={handleCopyLink}
									title="Click to copy task link"
								>
									{shortTaskRef(task, project)}
									<Link className="h-3 w-3" />
								</span>

								{task.ticket_code && (
									<TicketCode code={task.ticket_code} />
								)}

								{currentStage && (
									<StageChip
										label={currentStage.name}
										color={currentStage.color}
									/>
								)}
								<InlinePriority
									value={task.priority}
									canEdit={canEdit}
									onSave={(p) => persist({ priority: p })}
								/>
								{/* <InlinePriority
									value={task.priority}
									canEdit={canEdit}
									onSave={(p) => persist({ priority: p })}
								/>
								{currentStage && (
									<StageChip
										label={currentStage.name}
										color={currentStage.color}
									/>
								)}
								<span
									className="flex cursor-pointer items-center gap-2 truncate text-sm font-medium text-primary hover:text-primary-hover"
									onClick={handleCopyLink}
									title="Click to copy task link"
								>
									{shortTaskRef(task, project)}
									<Link className="h-4 w-4" />
								</span> */}
							</div>
							<div className="flex shrink-0 items-center gap-1">
								<Button
									variant="ghost"
									size="sm"
									onClick={handleToggleSubscribe}
									disabled={subscribePending}
								>
									{subscribePending ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : subscribed ? (
										<BellRing className="h-4 w-4" />
									) : (
										<Bell className="h-4 w-4" />
									)}
									{subscribed ? "Watching" : "Watch"}
								</Button>
								<CalendarButton
									projectId={task.project_id}
									taskId={task.id}
									taskTitle={task.title}
									estimatedTime={task.estimated_time}
								/>
								{onDelete && (
									<PermissionGate feature="Create & edit tasks">
										<Button
											variant="ghost"
											size="sm"
											className="text-danger hover:text-danger"
											onClick={() =>
												setConfirmingDelete(true)
											}
										>
											<Trash2 className="h-4 w-4" />
										</Button>
									</PermissionGate>
								)}
								<button
									type="button"
									onClick={onClose}
									className="rounded-md p-1.5 text-muted hover:bg-muted-subtle hover:text-foreground"
									aria-label="Close"
								>
									<X className="h-4 w-4" />
								</button>
							</div>
						</header>

						{/* Body */}
						<CopyTextContextMenu
							title={task.title}
							description={task.description}
							link={taskUrl}
							className="grid flex-1 grid-cols-1 overflow-y-auto md:grid-cols-[1fr_320px]"
						>
							{/* Main column */}
							<div className="min-w-0 space-y-7 px-6 py-6">
								{/* Parent backlink */}
								{task.parent_task_id && task.parent_task && (
									<button
										type="button"
										onClick={() =>
											onOpenParent?.(
												task.parent_task_id ?? "",
											)
										}
										disabled={!onOpenParent}
										className="inline-flex items-center justify-center text-left gap-1 text-xs text-primary hover:underline disabled:cursor-default disabled:opacity-60"
									>
										<CornerDownRight className="h-3 w-3" />
										Part of {task.parent_task.title}
									</button>
								)}

								{/* Title + description */}
								<div>
									<HoverCopy text={task.title} label="Title">
										<InlineTitle
											value={task.title}
											canEdit={canEdit}
											onSave={(v) => persist({ title: v })}
										/>
									</HoverCopy>
									<HoverCopy
										text={toMarkdown(task.description).trim()}
										label="Description"
									>
										<InlineDescription
											value={task.description ?? ""}
											canEdit={canEdit}
											onSave={(v) =>
												persist({ description: v })
											}
										/>
									</HoverCopy>
									{task.created_by && (
										<p className="mt-4 -mb-2 text-[10px] italic text-muted">
											Created by{" "}
											<span className="font-medium">
												{task.created_by.full_name ??
													task.created_by.email}
											</span>
										</p>
									)}
								</div>

								{/* Subtasks */}
								<section>
									<div className="mb-3 flex items-center justify-between">
										<div className="flex items-center gap-2">
											<ListChecks className="h-4 w-4 text-muted" />
											<h3 className="text-sm font-semibold text-foreground">
												Subtasks
											</h3>
											<span className="text-xs text-muted">
												({children.length})
											</span>
										</div>
										{children.length > 0 && (
											<span className="text-xs font-medium text-primary">
												{progress}% Complete
											</span>
										)}
									</div>
									{children.length > 0 && (
										<div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-muted-subtle">
											<div
												className="h-full bg-primary transition-all"
												style={{
													width: `${progress}%`,
												}}
											/>
										</div>
									)}
									{children.length === 0 ? (
										<div className="rounded-lg border border-dashed border-border px-4 py-6 text-center">
											<p className="mb-2 text-xs text-muted">
												No subtasks yet
											</p>
											{onAddChild && (
												<PermissionGate feature="Create & edit tasks">
													<Button
														variant="outline"
														size="sm"
														onClick={() =>
															onAddChild(task)
														}
													>
														<Plus className="mr-1 h-3 w-3" />
														Add subtask
													</Button>
												</PermissionGate>
											)}
										</div>
									) : (
										<div className="space-y-1.5 rounded-lg border border-border bg-muted-subtle/30 p-3">
											{children.map((child) => {
												const done =
													child.apiStatus === "done";
												const childStage =
													stageList.find(
														(s) =>
															s.key ===
															child.apiStatus,
													);
												return (
													<div
														key={child.id}
														className="group flex items-center gap-2.5"
													>
														<button
															type="button"
															onClick={() =>
																onToggleChildDone?.(
																	child,
																)
															}
															className={cn(
																"flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
																done
																	? "border-primary bg-primary text-white"
																	: "border-border-strong bg-surface hover:border-primary",
															)}
															aria-label={
																done
																	? "Mark not done"
																	: "Mark done"
															}
															disabled={
																!onToggleChildDone
															}
														>
															{done && (
																<Check className="h-3 w-3" />
															)}
														</button>
														<button
															type="button"
															onClick={() =>
																onOpenTask?.(
																	child,
																)
															}
															className={cn(
																"flex-1 truncate text-left text-sm transition-colors",
																done
																	? "text-muted line-through"
																	: "text-foreground hover:text-primary",
															)}
														>
															{child.title}
														</button>
														{childStage && (
															<span
																className="inline-flex shrink-0 items-center gap-1.5 text-[10px] font-medium"
																style={{
																	color: childStage.color,
																}}
															>
																<StageDot
																	color={
																		childStage.color
																	}
																/>
																{
																	childStage.name
																}
															</span>
														)}
													</div>
												);
											})}
											{onAddChild && (
												<PermissionGate feature="Create & edit tasks">
													<button
														type="button"
														onClick={() =>
															onAddChild(task)
														}
														className="flex items-center gap-2 pt-1.5 text-xs text-muted hover:text-primary"
													>
														<Plus className="h-3 w-3" />
														Add subtask
													</button>
												</PermissionGate>
											)}
										</div>
									)}
								</section>

								{/* Checklist */}
								<ChecklistSection
									projectId={task.project_id}
									taskId={task.id}
									canEdit={canEdit}
								/>

								{/* Attachments */}
								<section>
									<div className="mb-3 flex items-center gap-2">
										<Paperclip className="h-4 w-4 text-muted" />
										<h3 className="text-sm font-semibold text-foreground">
											Attachments
										</h3>
									</div>
									<TaskAttachments
										projectId={task.project_id}
										taskId={task.id}
										canEdit={canEdit}
										pasteEnabled={escEnabled}
									/>
								</section>

								{/* GitHub */}
								<GithubSection
									projectId={task.project_id}
									taskId={task.id}
									canEdit={canEdit}
								/>

								{/* Developer notes */}
								<section>
									<div className="mb-3 flex items-center justify-between">
										<div className="flex items-center gap-2">
											<MessageSquare className="h-4 w-4 text-muted" />
											<h3 className="text-sm font-semibold text-foreground">
												Developer's Notes
											</h3>
										</div>
										<PermissionGate feature="Create & edit tasks">
											<Button
												variant="primary_outline"
												size="sm"
												onClick={() =>
													setShowNotesEditor(
														(v) => !v,
													)
												}
											>
												{showNotesEditor
													? "Cancel"
													: "Edit notes"}
											</Button>
										</PermissionGate>
									</div>
									{showNotesEditor ? (
										<>
											<ProjectDescriptionEditor
												value={notes}
												onChange={setNotes}
												placeholder="Add implementation details, technical context, or notes for the dev team..."
											/>
											{onSaveNotes && (
												<div className="mt-3 flex justify-end">
													<Button
														onClick={handleSave}
														disabled={saving}
													>
														{saving && (
															<Loader2 className="mr-2 h-4 w-4 animate-spin" />
														)}
														Save Notes
													</Button>
												</div>
											)}
										</>
									) : projectDescriptionText(notes) ? (
										<div className="rounded-lg bg-muted-subtle/40 p-3 text-sm text-foreground">
											<ProjectDescriptionPreview
												value={notes}
											/>
										</div>
									) : (
										<p className="text-xs italic text-muted">
											No notes yet.
										</p>
									)}
								</section>

								{/* Comments */}
								<section>
									<div className="mb-3 flex items-center gap-2">
										<MessagesSquare className="h-4 w-4 text-muted" />
										<h3 className="text-sm font-semibold text-foreground">
											Comments
										</h3>
									</div>
									<TaskComments
										projectId={task.project_id}
										taskId={task.id}
									/>
								</section>

								{/* History */}
								<TaskHistorySection
									projectId={task.project_id}
									taskId={task.id}
									refreshKey={task.updated_at}
								/>
							</div>

							{/* Meta rail */}
							<aside className="border-t border-border bg-muted-subtle/30 px-5 py-5 md:border-l md:border-t-0">
								<div className="divide-y divide-border">
									{/* Status */}
									<MetaRow
										icon={
											<CircleDot className="h-3.5 w-3.5" />
										}
										label="Status"
									>
										{canEditStatus ? (
											<div className="flex items-center gap-2">
												<div className="min-w-0 flex-1">
													<SearchableSelect<{
														color: string;
													}>
														value={task.apiStatus}
														onValueChange={
															handleStatusChange
														}
														disabled={statusSaving}
														size="sm"
														placeholder="Select status"
														options={stageList.map(
															(stage) => ({
																value: stage.key,
																label: stage.name,
																meta: {
																	color: stage.color,
																},
															}),
														)}
														renderOption={(opt) => (
															<StageChip
																label={
																	opt.label
																}
																color={
																	opt.meta
																		?.color ??
																	""
																}
															/>
														)}
														renderValue={(opt) => (
															<span
																className="inline-flex items-center gap-2 font-medium"
																style={{
																	color: opt
																		.meta
																		?.color,
																}}
															>
																<StageDot
																	color={
																		opt.meta
																			?.color ??
																		""
																	}
																/>
																{opt.label}
															</span>
														)}
													/>
												</div>
												{statusSaving && (
													<Loader2 className="h-3.5 w-3.5 animate-spin text-muted" />
												)}
											</div>
										) : currentStage ? (
											<StageChip
												label={currentStage.name}
												color={currentStage.color}
											/>
										) : null}
									</MetaRow>

									{/* Sub-Status */}
									{(availableSubStatuses.length > 0 ||
										currentSubStatus) && (
										<MetaRow
											icon={
												<CircleDot className="h-3.5 w-3.5" />
											}
											label="Sub-Status"
										>
											{canEditStatus &&
											availableSubStatuses.length > 0 ? (
												<div className="flex items-center gap-2">
													<div className="min-w-0 flex-1">
														<SearchableSelect<{
															color: string;
														}>
															value={
																task.sub_status ??
																""
															}
															onValueChange={
																handleSubStatusChange
															}
															disabled={
																subStatusSaving
															}
															size="sm"
															clearable
															placeholder="None"
															options={availableSubStatuses.map(
																(s) => ({
																	value: s.key,
																	label: s.name,
																	meta: {
																		color: s.color,
																	},
																}),
															)}
															renderOption={(
																opt,
															) => (
																<StageChip
																	label={
																		opt.label
																	}
																	color={
																		opt.meta
																			?.color ??
																		""
																	}
																/>
															)}
															renderValue={(
																opt,
															) => (
																<span
																	className="inline-flex items-center gap-2 font-medium"
																	style={{
																		color: opt
																			.meta
																			?.color,
																	}}
																>
																	<StageDot
																		color={
																			opt
																				.meta
																				?.color ??
																			""
																		}
																	/>
																	{opt.label}
																</span>
															)}
														/>
													</div>
													{subStatusSaving && (
														<Loader2 className="h-3.5 w-3.5 animate-spin text-muted" />
													)}
												</div>
											) : currentSubStatus ? (
												<StageChip
													label={
														currentSubStatus.name
													}
													color={
														currentSubStatus.color
													}
												/>
											) : null}
										</MetaRow>
									)}

									{/* Assignees */}
									<MetaRow
										icon={<User className="h-3.5 w-3.5" />}
										label="Assignees"
									>
										<InlineAssignee
											assignee={task.assigned_to}
											additionalAssignees={
												task.additional_assignees
											}
											profiles={profiles}
											canEdit={canEdit}
											onSave={(v) =>
												persist({ assigned_to: v })
											}
											onSaveAdditional={(ids) =>
												persist({ assignee_ids: ids })
											}
										/>
									</MetaRow>

									{/* Type */}
									{canEditStatus && (
										<MetaRow
											icon={
												<Shapes className="h-3.5 w-3.5" />
											}
											label="Type"
										>
											<InlineTaskType
												value={task.task_type}
												canEdit={canEdit}
												onSave={(id) =>
													persist({
														task_type_id: id,
													})
												}
											/>
										</MetaRow>
									)}

									{/* Project */}
									<MetaRow
										icon={
											<FolderKanban className="h-3.5 w-3.5" />
										}
										label="Project"
									>
										<InlineProject
											projectId={task.project_id}
											projects={projects}
											canEdit={canEdit}
											onSave={(v) =>
												persist({ project_id: v })
											}
										/>
									</MetaRow>

									{/* Sprint */}
									<MetaRow
										icon={
											<Rocket className="h-3.5 w-3.5" />
										}
										label="Sprint"
									>
										<InlineSprint
											sprint={task.sprint}
											sprints={sprints}
											loading={sprintsLoading}
											canEdit={canEdit}
											showSelectActive
											onSave={(v) =>
												persist({ sprint_id: v })
											}
										/>
									</MetaRow>

									{/* Due date */}
									<MetaRow
										icon={
											<Calendar className="h-3.5 w-3.5" />
										}
										label="Due date"
									>
										<InlineDueDate
											value={task.due_date}
											canEdit={canEdit}
											onSave={(v) =>
												persist({ due_date: v })
											}
										/>
									</MetaRow>

									{/* Estimate */}
									<MetaRow
										icon={<Clock className="h-3.5 w-3.5" />}
										label="Estimate"
									>
										<div className="flex items-center gap-2">
											<div className="min-w-0 flex-1">
												<InlineEstimatedTime
													value={
														task.active_sprint_estimated_time ??
														0
													}
													canEdit={canEdit}
													onSave={(v) =>
														persist({
															estimated_time: v,
														})
													}
												/>
											</div>
											<EstimateHistoryPopover
												projectId={task.project_id}
												taskId={task.id}
												canEdit={canEdit}
											/>
										</div>
									</MetaRow>

									{/* Log Time */}
									{flags.enable_time_logging && (
										<MetaRow
											icon={
												<Clock className="h-3.5 w-3.5" />
											}
											label="Log Time"
										>
											<InlineLogTime
												projectId={task.project_id}
												taskId={task.id}
												assigneeIds={allAssignees(
													task,
												).map((p) => p.id)}
											/>
										</MetaRow>
									)}

									{/* Tags */}
									<MetaRow
										icon={
											<TagIcon className="h-3.5 w-3.5" />
										}
										label="Tags"
									>
										<InlineTags
											value={task.tags}
											canEdit={canEdit}
											onSave={(v) => persist({ tags: v })}
										/>
									</MetaRow>

									<MetaRow
										icon={
											<CalendarPlus className="h-3.5 w-3.5" />
										}
										label="Date created"
									>
										<span className="text-xs text-foreground">
											{formatTimestamp(task.created_at)}
										</span>
									</MetaRow>

									<MetaRow
										icon={
											<History className="h-3.5 w-3.5" />
										}
										label="Date modified"
									>
										<span className="text-xs text-foreground">
											{formatTimestamp(task.updated_at)}
										</span>
									</MetaRow>
								</div>

								<Separator className="my-4" />

								<Button
									variant="outline"
									className="w-full justify-start"
									onClick={handleCopyLink}
								>
									<Link2 className="mr-2 h-4 w-4 text-primary" />
									<span className="text-primary text-xs!">
										Copy Task Link
									</span>
								</Button>
							</aside>
						</CopyTextContextMenu>
					</>
				)}
			</aside>

			<Dialog
				open={confirmingDelete && !!task}
				onOpenChange={(o) => {
					if (!o) setConfirmingDelete(false);
				}}
			>
				<DialogContent className="max-w-[420px]">
					<DialogHeader>
						<DialogTitle>Delete Task</DialogTitle>
						<DialogDescription>
							This will permanently delete{" "}
							<span className="font-medium text-foreground">
								{task?.title}
							</span>
							. This action cannot be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<DialogClose asChild>
							<Button variant="outline">Cancel</Button>
						</DialogClose>
						<Button
							variant="destructive"
							className="bg-danger text-white hover:bg-danger/90"
							onClick={() => {
								if (!task || !onDelete) return;
								setConfirmingDelete(false);
								onDelete(task);
							}}
						>
							<Trash2 className="mr-1.5 h-4 w-4" />
							Delete
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}

function formatTimestamp(value?: string | null) {
	return value ? format(parseISO(value), "MMM d, yyyy h:mm a") : "-";
}
